import { atomicApiUrl, contracts } from "../config";
import type { AtomicAsset, MarketSale } from "../types";

// Thin wrapper around the public AtomicAssets / AtomicMarket REST API that
// AtomicHub itself is built on. Docs: https://docs.atomicassets.io &
// https://docs.atomicmarket.io — endpoint shapes below are current as of
// this writing; double-check against those docs if something 404s, since
// this sandbox couldn't make a live network call to verify while building.

async function apiGet<T>(path: string, params: Record<string, string | number | undefined> = {}): Promise<T> {
  const url = new URL(atomicApiUrl + path);
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") url.searchParams.set(key, String(value));
  }
  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`AtomicHub API error ${res.status} for ${url.pathname}`);
  }
  const json = await res.json();
  if (json.success === false) {
    throw new Error(json.message || "AtomicHub API request failed");
  }
  // Found the hard way: for at least one single-resource endpoint
  // (/atomicassets/v1/schemas/{collection}/{schema} when the schema
  // doesn't exist but the collection does), AtomicHub's API returns HTTP
  // 200 with {"success":true} and NO "data" field at all — not the 404 or
  // {success:false} this wrapper otherwise treats as "not found." Since
  // `json.data` is then `undefined` rather than `null`, apiGetOrNull's
  // `!== null` checks (used everywhere as "does this already exist?")
  // would silently treat that as a real, existing object. Every real
  // response this API returns wraps an object or array in `data`, so
  // `undefined` here always means "nothing was actually returned," never
  // a legitimately absent field on a real result.
  if (json.data === undefined) {
    throw new Error(`AtomicHub API returned no data for ${url.pathname} (likely not found)`);
  }
  return json.data as T;
}

/** Same as apiGet, but a 404/"not found" comes back as `null` instead of
 * throwing — used for "does this already exist?" checks (a missing
 * collection/schema is an expected, normal outcome, not an error). */
async function apiGetOrNull<T>(path: string, params: Record<string, string | number | undefined> = {}): Promise<T | null> {
  try {
    return await apiGet<T>(path, params);
  } catch {
    return null;
  }
}

export interface ListSalesOptions {
  collectionName?: string;
  owner?: string;
  seller?: string;
  assetId?: string;
  page?: number;
  limit?: number;
  order?: "asc" | "desc";
  sort?: "created" | "price" | "updated";
}

/** Active marketplace listings (state 1 = LISTED, i.e. buyable now). */
export function listActiveSales(opts: ListSalesOptions = {}): Promise<MarketSale[]> {
  return apiGet<MarketSale[]>("/atomicmarket/v1/sales", {
    state: 1,
    collection_name: opts.collectionName,
    seller: opts.seller,
    asset_id: opts.assetId,
    page: opts.page ?? 1,
    limit: opts.limit ?? 24,
    order: opts.order ?? "desc",
    sort: opts.sort ?? "created",
  });
}

export function getSale(saleId: string): Promise<MarketSale> {
  return apiGet<MarketSale>(`/atomicmarket/v1/sales/${saleId}`);
}

/** Completed sales (state 3 = SOLD), newest first — powers the "Recently
 * Sold" tab. Deliberately unfiltered by collection/seller: this is meant
 * to show the whole chain's activity (like AtomicHub's own feed), not
 * just items that moved through this site. Verified live against
 * `/atomicmarket/v1/sales?state=3&sort=updated&order=desc` (Sept 2026) —
 * `updated_at_time` is what actually reflects "when it sold" for a sale
 * row, since AtomicMarket has no separate "sold_at" field of its own. */
export function listRecentSales(limit = 100): Promise<MarketSale[]> {
  return apiGet<MarketSale[]>("/atomicmarket/v1/sales", {
    state: 3,
    page: 1,
    limit,
    order: "desc",
    sort: "updated",
  });
}

export interface ListAssetsOptions {
  owner?: string;
  collectionName?: string;
  page?: number;
  limit?: number;
}

/** Assets owned by a given account (used for the "My Assets" inventory page). */
export function listOwnedAssets(opts: ListAssetsOptions): Promise<AtomicAsset[]> {
  return apiGet<AtomicAsset[]>("/atomicassets/v1/assets", {
    owner: opts.owner,
    collection_name: opts.collectionName,
    page: opts.page ?? 1,
    limit: opts.limit ?? 100,
    order: "desc",
    sort: "updated",
  });
}

export function getAsset(assetId: string): Promise<AtomicAsset> {
  return apiGet<AtomicAsset>(`/atomicassets/v1/assets/${assetId}`);
}

/** Display metadata (name/img/description/max_supply/issued_supply) for a
 * template — used to show drops nicely without duplicating that data on
 * nftdrops' own (deliberately minimal) on-chain table. */
export function getTemplate(collectionName: string, templateId: number | string) {
  return apiGet<import("../types").AtomicTemplate>(
    `/atomicassets/v1/templates/${collectionName}/${templateId}`
  );
}

/** Does this collection already exist? Used by the Create NFT wizard to
 * decide whether a `createcol` step is needed, and (if it exists) to read
 * its `authorized_accounts`/`notify_accounts` so escrow authorization
 * steps can be skipped once already granted. Returns `null`, not an
 * error, if there's no such collection yet. */
export function getCollection(collectionName: string) {
  return apiGetOrNull<import("../types").AtomicCollection>(`/atomicassets/v1/collections/${collectionName}`);
}

/** Does this schema already exist under this collection? Returns `null`,
 * not an error, if not. */
export function getSchema(collectionName: string, schemaName: string) {
  return apiGetOrNull<import("../types").AtomicSchema>(`/atomicassets/v1/schemas/${collectionName}/${schemaName}`);
}

/**
 * The Create NFT wizard's backed-NFT path needs the `template_id` a
 * just-broadcast `createtempl` action assigned before it can register a
 * backing rate or mint — but that ID is assigned on-chain, not known
 * client-side ahead of time, and the AtomicHub index takes a little while
 * to catch up after a transaction confirms. This polls for the newest
 * template under a collection/schema a few times with a delay, rather
 * than assuming it's indexed instantly.
 *
 * NOT verified end-to-end against a live indexer in this sandbox (no
 * network access to test against) — the attempt count/delay below are a
 * reasonable starting guess, not a measured value. If this comes back
 * `null` in practice, increase `attempts`/`delayMs`, or read the
 * `lognewtempl` notification's `template_id` straight out of the
 * transaction trace instead (more precise, but depends on exactly what
 * your WharfKit/node version returns for `session.transact()` — worth
 * switching to once you can test against testnet).
 */
export async function waitForLatestTemplate(
  collectionName: string,
  schemaName: string,
  opts: { attempts?: number; delayMs?: number } = {}
): Promise<import("../types").AtomicTemplate | null> {
  const attempts = opts.attempts ?? 6;
  const delayMs = opts.delayMs ?? 2000;
  for (let i = 0; i < attempts; i++) {
    const rows = await apiGet<import("../types").AtomicTemplate[]>("/atomicassets/v1/templates", {
      collection_name: collectionName,
      schema_name: schemaName,
      page: 1,
      limit: 1,
      order: "desc",
      sort: "created",
    });
    if (rows[0]) return rows[0];
    if (i < attempts - 1) await new Promise((r) => setTimeout(r, delayMs));
  }
  return null;
}

/**
 * Read live on-chain balances/rows directly from the chain RPC (bypasses the
 * AtomicHub index, useful for things like escrow-contract state that has no
 * dedicated REST API of its own yet).
 */
export async function getTableRows<T = Record<string, unknown>>(params: {
  rpcUrl: string;
  code: string;
  scope: string;
  table: string;
  limit?: number;
  lower_bound?: string;
  upper_bound?: string;
  index_position?: string;
  key_type?: string;
}): Promise<T[]> {
  const res = await fetch(`${params.rpcUrl}/v1/chain/get_table_rows`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      json: true,
      code: params.code,
      scope: params.scope,
      table: params.table,
      limit: params.limit ?? 100,
      lower_bound: params.lower_bound,
      upper_bound: params.upper_bound,
      index_position: params.index_position,
      key_type: params.key_type,
    }),
  });
  if (!res.ok) throw new Error(`Chain RPC error ${res.status}`);
  const json = await res.json();
  return json.rows as T[];
}

/**
 * Is there an actual contract (WASM code) set on this account right now?
 * Used to show an accurate "escrowbacker isn't deployed yet" warning
 * instead of a hardcoded one.
 *
 * Node versions differ on how they expose this in `get_account`: older/some
 * nodes return `code_hash` (all-zero = no contract); current WAX mainnet
 * nodes tested here (Sept 2026) omit `code_hash` entirely and only expose
 * `last_code_update`, which stays at the epoch ("1970-01-01T00:00:00.000")
 * until a contract is ever deployed and gets bumped to a real timestamp on
 * every setcode after that. This checks whichever field is actually present
 * rather than assuming `code_hash` exists — the earlier version of this
 * function always returned false in practice because it only checked
 * `code_hash`, which this endpoint doesn't send.
 *
 * Fails safe (returns false, i.e. "assume not deployed / show the warning")
 * on any network error, since that's the safer default for a warning
 * banner.
 */
export async function hasContractDeployed(rpcUrl: string, account: string): Promise<boolean> {
  // get_code_hash is a tiny, reliable call (get_account was intermittently
  // failing on some nodes and producing a false "no contract deployed" warning).
  const urls = [rpcUrl, "https://wax.greymass.com"].filter((u, i, a) => a.indexOf(u) === i);
  for (const url of urls) {
    try {
      const res = await fetch(`${url}/v1/chain/get_code_hash`, {
        method: "POST",
        body: JSON.stringify({ account_name: account }),
      });
      if (!res.ok) continue;
      const json = await res.json();
      if (typeof json.code_hash === "string") return /[^0]/.test(json.code_hash);
    } catch {
      /* try next node */
    }
  }
  return false;
}

export interface HotCollection {
  collectionName: string;
  displayName: string;
  image?: string;
  saleCount: number;
  /** Human-readable totals per token symbol, e.g. { WAX: 142.5 } — kept
   * per-symbol rather than converted to one number, since this app has no
   * USD price-feed to convert WAX/EOS into a single comparable figure. */
  volumeBySymbol: Record<string, number>;
}

/**
 * "Hottest collections" ranking, AtomicHub-style (its own "Hottest
 * Collections — 24h" row is the direct inspiration) — collections with
 * the most traded value in the last `hours` hours, chain-wide.
 *
 * Uses AtomicMarket's own purpose-built `stats/collections` endpoint
 * rather than paginating raw sales and aggregating client-side (an
 * earlier version of this function did that, and produced a materially
 * different ranking from AtomicHub's own displayed list — this endpoint
 * is very likely what AtomicHub's frontend itself calls, and matching it
 * exactly beats reverse-engineering the same ranking by hand).
 *
 * Two things verified live against this endpoint (Sept 2026) that aren't
 * obvious from its shape alone:
 * - Its own `period=24h`-style query param is silently ignored (confirmed:
 *   identical results with or without it) — `after=<epoch ms>` is what
 *   actually bounds the window, same as the plain /v1/sales endpoint.
 * - With no explicit `sort`, results come back ranked by traded *volume*,
 *   not sale count — a collection with 8 expensive sales can outrank one
 *   with 100 cheap ones. That's the actual reason a naive count-based
 *   ranking (this function's first version) disagreed with AtomicHub's
 *   displayed list: it was measuring a different thing, not stale/wrong
 *   data.
 *
 * Known limitation: only WAX-denominated volume (`symbol=WAX`) — the same
 * endpoint 500-errored on `symbol=EOS` when tested live, and EOS-priced
 * sales are rare enough on WAX's NFT market that this doesn't change the
 * ranking in practice, but a collection selling exclusively in EOS would
 * be invisible here.
 */
export async function getHotCollections(opts: { hours?: number; limit?: number } = {}): Promise<HotCollection[]> {
  const hours = opts.hours ?? 24;
  const limit = opts.limit ?? 12;
  const after = Date.now() - hours * 60 * 60 * 1000;

  // Unlike every other endpoint this file wraps, this one's real shape is
  // `{success, data: {symbol: {...}, results: [...]}}` — the array is
  // nested under `data.results`, not `data` itself — so it's typed as an
  // object here rather than reusing apiGet<T[]>'s "data IS the array"
  // assumption (confirmed via a live response, not guessed from docs).
  const response = await apiGet<{ results: Array<{ collection_name: string; name?: string; img?: string; sales: string; volume: string }> }>(
    "/atomicmarket/v1/stats/collections",
    { symbol: "WAX", after, limit }
  );
  const rows = response.results ?? [];

  return rows.map((r) => ({
    collectionName: r.collection_name,
    displayName: r.name || r.collection_name,
    image: r.img,
    saleCount: Number(r.sales),
    volumeBySymbol: { WAX: Number(r.volume) / 10 ** 8 },
  }));
}

/** Fetch a WAX/EOS account's liquid token balance, e.g. for the Mint page. */
export async function getTokenBalance(
  rpcUrl: string,
  tokenContract: string,
  account: string,
  symbol: string
): Promise<string | null> {
  const rows = await getTableRows<{ balance: string }>({
    rpcUrl,
    code: tokenContract,
    scope: account,
    table: "accounts",
  });
  const row = rows.find((r) => r.balance.endsWith(` ${symbol}`));
  return row ? row.balance : null;
}

/**
 * Is `marketplaceName` already registered on the live AtomicMarket
 * contract's own `marketplaces` table? That table is a single global
 * scope (the contract's own account) keyed by marketplace_name, so this
 * is one direct chain read, not an index query — reads straight past
 * whatever the AtomicHub index has caught up to.
 *
 * Verified live: `GET /atomicmarket/v1/marketplaces` (Sept 2026) returns
 * rows shaped `{marketplace_name, creator}`, and plenty of registered
 * marketplaces use their own account name as the marketplace_name (e.g.
 * "waxplorercom" registered by "waxplorercom") — the same pattern this
 * function assumes for the `lower_bound`/`upper_bound` lookup.
 */
export async function isMarketplaceRegistered(rpcUrl: string, marketplaceName: string): Promise<boolean> {
  if (!marketplaceName) return false;
  const rows = await getTableRows<{ marketplace_name: string; creator: string }>({
    rpcUrl,
    code: contracts.atomicmarket,
    scope: contracts.atomicmarket,
    table: "marketplaces",
    lower_bound: marketplaceName,
    upper_bound: marketplaceName,
    limit: 1,
  });
  return rows.length > 0;
}

/**
 * This account's claimable AtomicMarket balance — the accumulated maker/
 * taker marketplace fee cut from every sale it facilitated, sitting in
 * AtomicMarket's own internal `balances` table until claimed with the
 * `withdraw` action (see registerMarketplace/withdrawMarketplaceBalance
 * in api/actions.ts). This is NOT a liquid wallet balance; it only shows
 * up here, never in a normal WAX wallet, until it's withdrawn.
 */
export async function getMarketplaceBalance(rpcUrl: string, account: string): Promise<string[]> {
  const rows = await getTableRows<{ owner: string; quantities: string[] }>({
    rpcUrl,
    code: contracts.atomicmarket,
    scope: contracts.atomicmarket,
    table: "balances",
    lower_bound: account,
    upper_bound: account,
    limit: 1,
  });
  return rows[0]?.quantities ?? [];
}

export interface CollectionOption {
  collectionName: string;
  displayName: string;
}

/**
 * Type-ahead collection search for the Marketplace filter bar — lets a
 * visitor filter listings down to one collection by name without already
 * knowing its exact `collection_name` (which is often not the same as the
 * human-readable display name AtomicHub shows). `match` does a partial,
 * case-insensitive search server-side against both the collection_name and
 * display name; kept to a small limit since this only backs an autocomplete
 * dropdown, not a full browse listing.
 */
export async function searchCollections(query: string, limit = 8): Promise<CollectionOption[]> {
  if (!query.trim()) return [];
  const rows = await apiGet<Array<{ collection_name: string; name?: string }>>("/atomicassets/v1/collections", {
    match: query.trim(),
    limit,
  });
  return rows.map((r) => ({ collectionName: r.collection_name, displayName: r.name || r.collection_name }));
}

export const atomicassetsContract = contracts.atomicassets;
export const atomicmarketContract = contracts.atomicmarket;
